/**
 * The named steps a removal walks, in the words the user is shown.
 *
 * ONE definition, used in three places on purpose: as the `operationLabel` a
 * step times out under, as the progress line the caller renders while that step
 * is in flight, and as the phase CMMD's persisted receipt is translated into.
 * Those strings drifting apart is how "Removing the project from Forge Cloud"
 * ends up reporting a stall in a step called something else — so there is only
 * one string to drift.
 *
 * A leaf module for the same reason. The saga that performs a removal and the
 * module that decides what to render both need these labels, and if either
 * owned them the other would have to import from it, which is exactly the cycle
 * that used to sit here.
 *
 * This is user-facing copy on the slowest destructive action in the sidebar.
 * CMMD persists a receipt before provider work begins, then the browser polls
 * bounded requests while the durable reconciler may continue for several
 * minutes, including after navigation or reload. Naming CMMD's current phase
 * makes that asynchronous work legible instead of looking like a dead button.
 */
export const REMOVAL_STEP_LABELS = {
  readCatalog: "Reading the Forge Cloud project list",
  unregister: "Starting the durable Forge Cloud removal",
  deleteCheckoutFiles: "Deleting checkout files",
  destroyRuntime: "Destroying the cloud runtime",
  purgeExecutionState: "Purging runtime execution state",
  deletePlanningProject: "Deleting the CMMD planning project",
  waitForRemoval: "Waiting for CMMD to finish removal",
  confirm: "Confirming the project left Forge Cloud",
  deleteForgeProject: "Removing the Forge project",
  reconcileSidebar: "Updating the project list",
} as const;

/**
 * A step label from `REMOVAL_STEP_LABELS` — never a free-form string.
 *
 * The TYPE is exported; the object deliberately is not. Consumers render
 * whatever label the progress reporter hands them, so nothing outside this
 * module needs to name a step — and a narrow union means a caller (or a test)
 * that invents a label the product never emits fails to compile instead of
 * asserting against fiction.
 */
export type RemovalStepLabel = (typeof REMOVAL_STEP_LABELS)[keyof typeof REMOVAL_STEP_LABELS];
